import React, { Suspense, lazy, useEffect, useState } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';
import { createSignedUrlWithRenewal, type SignedUrlHandle } from '@/lib/storage/signed-url';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

// Lazy-load react-pdf wrapper — keeps ~422kb pdf chunk out of the initial
// bundle. Only fetched when TermsModal opens with a dynamic document.
const PdfViewer = lazy(() => import('./PdfViewer'));

const TERMS_BUCKET = 'terms-documents';
const SIGNED_URL_TTL_SECONDS = 7200;
// Após este intervalo sem indício de falha explícita, mostra dica manual
// "PDF não carregou? Trocar visualização" sobre o iframe. iOS Safari e
// alguns Chrome Android PWA standalone disparam onLoad mesmo entregando
// página em branco — sem detecção same-origin do conteúdo (signed URL é
// cross-origin), o fallback fica gated em ação do usuário.
const IFRAME_FALLBACK_HINT_MS = 4000;

/**
 * Detecta plataformas onde <iframe src="...pdf"> notoriamente falha:
 *  - iOS Safari (qualquer contexto): nunca renderiza PDF em iframe.
 *  - PWA standalone (display-mode: standalone): Android Chrome em modo
 *    instalado frequentemente baixa o PDF em vez de exibir.
 * Quando true, abre direto no PdfViewer canvas (react-pdf).
 */
function detectIframePdfUnsupported(): boolean {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') return false;
  const ua = navigator.userAgent;
  const isIOS =
    /iPad|iPhone|iPod/.test(ua) ||
    (ua.includes('Mac') && typeof document !== 'undefined' && 'ontouchend' in document);
  const isStandalone =
    window.matchMedia?.('(display-mode: standalone)').matches ||
    (window.navigator as { standalone?: boolean }).standalone === true;
  return isIOS || isStandalone;
}

export interface DynamicTermsViewerProps {
  storagePath: string;
  /**
   * Owned by TermsModal — used so the legacy 90%-scroll handler keeps
   * receiving native scroll events from the parent overflow container.
   * Not consumed directly here, but exposed for documentation/typing.
   */
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
  onScrollToEnd?: () => void;
  className?: string;
}

/**
 * Responsive PDF viewer:
 *  - Desktop (>= md / 768px): react-pdf canvas with scroll-tracking.
 *  - Mobile (< md): hybrid — iOS / PWA standalone fall straight to the
 *    canvas; other mobile browsers try iframe first (native zoom/scroll),
 *    with manual + auto fallback to canvas if rendering fails.
 *
 * Both branches consume signed URLs with auto-renewal on 403 (BUG-6).
 */
export const DynamicTermsViewer: React.FC<DynamicTermsViewerProps> = ({
  storagePath,
  onScrollToEnd,
  className,
}) => {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <MobileViewer
        storagePath={storagePath}
        onScrollToEnd={onScrollToEnd}
        className={className}
      />
    );
  }

  return (
    <div
      data-testid="terms-pdf-viewer"
      className={cn('h-full w-full', className)}
    >
      <Suspense fallback={<Skeleton className="h-full w-full" />}>
        <PdfViewer
          storagePath={storagePath}
          onScrollToEnd={onScrollToEnd}
        />
      </Suspense>
    </div>
  );
};

interface MobileViewerProps {
  storagePath: string;
  onScrollToEnd?: () => void;
  className?: string;
}

/**
 * Mobile branch: decide entre iframe (default em browsers compatíveis) e
 * canvas (iOS/PWA standalone OU fallback manual disparado pelo usuário).
 */
const MobileViewer: React.FC<MobileViewerProps> = ({
  storagePath,
  onScrollToEnd,
  className,
}) => {
  const [useCanvas, setUseCanvas] = useState<boolean>(() =>
    detectIframePdfUnsupported(),
  );

  if (useCanvas) {
    return (
      <div
        data-testid="terms-pdf-viewer"
        className={cn('h-full w-full', className)}
      >
        <Suspense fallback={<Skeleton className="h-full w-full" />}>
          <PdfViewer
            storagePath={storagePath}
            onScrollToEnd={onScrollToEnd}
          />
        </Suspense>
      </div>
    );
  }

  return (
    <MobileIframeViewer
      storagePath={storagePath}
      onScrollToEnd={onScrollToEnd}
      className={className}
      onSwitchToCanvas={() => setUseCanvas(true)}
    />
  );
};

interface MobileIframeViewerProps {
  storagePath: string;
  onScrollToEnd?: () => void;
  className?: string;
  /** Disparado pelo usuário (botão) ou por erro de carga — vira para canvas. */
  onSwitchToCanvas: () => void;
}

const MobileIframeViewer: React.FC<MobileIframeViewerProps> = ({
  storagePath,
  onScrollToEnd,
  className,
  onSwitchToCanvas,
}) => {
  const [handle, setHandle] = useState<SignedUrlHandle | null>(null);
  const [iframeKey, setIframeKey] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [showFallbackHint, setShowFallbackHint] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setHandle(null);
    setShowFallbackHint(false);

    createSignedUrlWithRenewal({
      bucket: TERMS_BUCKET,
      path: storagePath,
      expiresIn: SIGNED_URL_TTL_SECONDS,
      // Phase 9-04: route through get-terms-signed-url EF (Option A bucket
      // gateway). Mobile iframe path also uses the gateway to keep parity
      // with desktop — bucket SELECT remains admin-only.
      useEdgeFunction: true,
    })
      .then((h) => {
        if (!cancelled) setHandle(h);
      })
      .catch(() => {
        if (!cancelled) setError('Não foi possível carregar o documento.');
      });

    return () => {
      cancelled = true;
    };
  }, [storagePath]);

  // Após handle pronto, agenda dica de fallback. iframe onLoad dispara mesmo
  // quando navegador entrega blob em branco (Android PWA), então este timer
  // garante que usuário sempre tenha rota de escape para o canvas.
  useEffect(() => {
    if (!handle) return;
    const t = window.setTimeout(
      () => setShowFallbackHint(true),
      IFRAME_FALLBACK_HINT_MS,
    );
    return () => window.clearTimeout(t);
  }, [handle, iframeKey]);

  // Browser controls scroll inside the iframe (cross-origin sandboxing
  // blocks JS from observing it). Trade-off documented in 09-CONTEXT.md:
  // mobile users gate on the 15s timer + explicit checkbox instead of
  // scroll-to-end. We fire onScrollToEnd as soon as the iframe loads to
  // unblock the modal's hasReachedEnd flag.
  const handleIframeLoad = () => {
    onScrollToEnd?.();
  };

  const handleIframeError = async () => {
    if (!handle) {
      onSwitchToCanvas();
      return;
    }
    try {
      await handle.refresh();
      setIframeKey((k) => k + 1);
    } catch {
      onSwitchToCanvas();
    }
  };

  if (error) {
    return (
      <div
        data-testid="terms-pdf-viewer"
        className={cn(
          'flex flex-col items-start gap-2 p-4 text-sm text-destructive',
          className,
        )}
        role="alert"
      >
        <span>{error}</span>
        <Button size="sm" variant="outline" onClick={onSwitchToCanvas}>
          Tentar visualização alternativa
        </Button>
      </div>
    );
  }

  if (!handle) {
    return (
      <div data-testid="terms-pdf-viewer" className={cn('h-full w-full', className)}>
        <Skeleton className="h-full w-full" />
      </div>
    );
  }

  return (
    <div
      data-testid="terms-pdf-viewer"
      className={cn('relative h-full w-full', className)}
    >
      <iframe
        key={iframeKey}
        src={handle.url}
        title="Termos de Uso"
        className="h-full w-full border-0"
        onLoad={handleIframeLoad}
        onError={handleIframeError}
      />
      {showFallbackHint && (
        <div className="pointer-events-auto absolute bottom-2 left-1/2 flex -translate-x-1/2 items-center gap-2 rounded-md border bg-background/95 px-3 py-1.5 text-xs shadow-md">
          <span className="text-muted-foreground">PDF não carregou?</span>
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs"
            onClick={onSwitchToCanvas}
          >
            Trocar visualização
          </Button>
        </div>
      )}
    </div>
  );
};

export default DynamicTermsViewer;
